// Themen-Taxonomie für die Content-Formulare — EINE Quelle statt des hartkodierten Enums.
//
// ⚠️ Bis 2026-08-11 lasen AudioForm, VideoForm und WebComponentForm die Kategorien aus
// `types/index.ts` (Enum + Label-Maps). Gleichzeitig gab es eine CMS-Seite, die eine ZWEITE Liste
// im localStorage pflegte, und in premium-web eine DRITTE als Konstante — letztere bereits
// auseinandergelaufen (37 statt 48 Einträge). Dieser Hook holt die Liste aus der Datenbank.
// Plan: docs/plans/plan-themen-kategorien-2026-08.md
import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { getApiBaseUrl } from '../utils/apiConfig';
import { getAccessToken } from '../lib/authToken';
import { getErrorMessage } from '../utils/errors';

interface SubNode {
  key: string;
  label: string;
  isActive: boolean;
}
interface MainNode {
  key: string;
  label: string;
  isActive: boolean;
  subcategories: SubNode[];
}

// ⚠️ Der Hook trennt zwei Fragen, die leicht zu einer verschmelzen — und dann ist `isActive`
// wirkungslos: „was darf man NEU wählen?" (nur Aktives) und „wie heißt dieser Slug?" (auch
// Deaktiviertes, sonst verlöre eine bestehende Zuordnung ihr Label). Deshalb liefert der Baum
// alles, und die wählbare Menge wird hier daraus abgeleitet — nicht von der Abfrage bestimmt.
export interface TopicTaxonomy {
  /** WÄHLBARE Hauptthemen-Slugs in Anzeigereihenfolge — deaktivierte sind nicht dabei. */
  mainKeys: string[];
  /** WÄHLBARE Unterthemen-Slugs eines Hauptthemas. */
  subKeysFor: (mainKey: string) => string[];
  /** Anzeigename eines Slugs — Haupt- wie Unterthema, auch deaktivierte. */
  labelFor: (key: string) => string;
  /**
   * Slugs, die am Inhalt hängen, die es noch gibt, die aber abgeschaltet wurden.
   *
   * Sie gehören NICHT in die Auswahl (sonst bewirkte das Deaktivieren nichts), müssen aber
   * sichtbar bleiben — aus demselben Grund wie `unknownSelected`.
   */
  deactivatedSelected: (selected: string[]) => string[];
  /**
   * Slugs, die am Inhalt hängen, die Taxonomie aber nicht kennt.
   *
   * ⚠️ Der Grund, warum es diese Funktion gibt: das Formular schickt beim Speichern die Liste
   * zurück, die es rendert. Würde es einen unbekannten Slug einfach nicht anzeigen, verschwände
   * er beim nächsten Speichern STILL aus dem Inhalt. Er muss also sichtbar bleiben — abwählbar,
   * aber nicht heimlich weg. Der Server-Wächter (`topicCategoryGuard.ts`) hält seine Seite der
   * Abmachung: er behält, was schon am Inhalt hing, auch wenn die Taxonomie es nicht kennt.
   */
  unknownSelected: (selected: string[]) => string[];
  isLoading: boolean;
  error: string | null;
  reload: () => void;
}

const api = axios.create({ baseURL: getApiBaseUrl(), timeout: 15000 });
api.interceptors.request.use(async (config) => {
  const token = await getAccessToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export function useTopicTaxonomy(): TopicTaxonomy {
  const [tree, setTree] = useState<MainNode[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      // `includeInactive=true` ist PFLICHT, nicht Bequemlichkeit: zeigt ein bestehender Inhalt auf
      // ein deaktiviertes Thema, muss es sichtbar bleiben und sein Label behalten — sonst fiele
      // die Zuordnung beim nächsten Speichern still weg. Dass hier ALLES ankommt, heißt NICHT,
      // dass alles wählbar ist; das entscheidet `selectableSubKeys`.
      const res = await api.get<{ data: MainNode[] }>('/api/topic-categories?includeInactive=true');
      setTree(res.data.data ?? []);
    } catch (e) {
      setError(getErrorMessage(e, 'Themen-Kategorien konnten nicht geladen werden.'));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const labels = useMemo(() => {
    const m = new Map<string, string>();
    for (const main of tree) {
      m.set(main.key, main.label);
      for (const sub of main.subcategories) m.set(sub.key, sub.label);
    }
    return m;
  }, [tree]);

  // Nur aktive Hauptthemen mit ihren aktiven Unterthemen. Ein Unterthema unter einem
  // deaktivierten Hauptthema bleibt außen vor, auch wenn es selbst aktiv ist: das Abschalten
  // eines Hauptthemas nimmt die Unterthemen mit, ein einzeln wieder eingeschaltetes hätte in der
  // Auswahl aber keine Gruppe, unter der es erscheinen könnte.
  const subsByMain = useMemo(() => {
    const m = new Map<string, string[]>();
    for (const main of tree) {
      if (!main.isActive) continue;
      m.set(main.key, main.subcategories.filter((s) => s.isActive).map((s) => s.key));
    }
    return m;
  }, [tree]);

  const selectableSubKeys = useMemo(
    () => new Set([...subsByMain.values()].flat()),
    [subsByMain]
  );

  // ⚠️ ALLE Unterthemen-Slugs, aktiv wie deaktiviert — und bewusst OHNE die Hauptthemen.
  // `topicCategories` an einem Inhalt enthält per Modell nur Unterthemen; ein Hauptthemen-Slug
  // darin ist keine gültige Zuordnung, sondern Altlast. Würde man ihn über die gemeinsame
  // Label-Karte als „bekannt" durchgehen lassen, erschiene er in keinem der beiden Warnblöcke
  // und verschwände beim nächsten Speichern still — genau der Defekt, den diese Seite behebt.
  const knownSubKeys = useMemo(() => {
    const s = new Set<string>();
    for (const main of tree) for (const sub of main.subcategories) s.add(sub.key);
    return s;
  }, [tree]);

  const mainKeys = useMemo(() => tree.filter((t) => t.isActive).map((t) => t.key), [tree]);

  const subKeysFor = useCallback((mainKey: string) => subsByMain.get(mainKey) ?? [], [subsByMain]);

  // Fällt ein Slug aus der Taxonomie, zeigt das Formular den rohen Schlüssel — sichtbar falsch
  // ist besser als unsichtbar verschwunden.
  const labelFor = useCallback((key: string) => labels.get(key) ?? key, [labels]);

  // „Bekannt, aber nicht wählbar" — deckt deaktivierte Unterthemen ebenso ab wie solche unter
  // einem deaktivierten Hauptthema.
  const deactivatedSelected = useCallback(
    (selected: string[]) => selected.filter((k) => knownSubKeys.has(k) && !selectableSubKeys.has(k)),
    [knownSubKeys, selectableSubKeys]
  );

  // Gegen die Unterthemen-Menge, nicht gegen die Label-Karte: sonst gälte ein Hauptthemen-Slug
  // als „bekannt" und fiele durch BEIDE Warnblöcke — sichtbar wäre er dann nirgends mehr.
  const unknownSelected = useCallback(
    (selected: string[]) => selected.filter((k) => !knownSubKeys.has(k)),
    [knownSubKeys]
  );

  return {
    mainKeys,
    subKeysFor,
    labelFor,
    deactivatedSelected,
    unknownSelected,
    isLoading,
    error,
    reload: () => void load(),
  };
}
